import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import Home from '../views/Home.vue'
import type { RouteLocationNormalized } from 'vue-router'

// Lazy-loaded components
const LoginView = () => import('@/views/LoginView.vue')
const DashboardView = () => import('@/views/DashboardView.vue')
const LoanApplicationView = () => import('@/views/LoanApplicationView.vue')
const LoanApplicationEditView = () => import('@/views/LoanApplicationEditView.vue')
const LoanApplicationUploadView = () => import('@/views/LoanApplicationUploadView.vue')
const LoanApplicationCreateView = () => import('@/views/LoanApplicationCreateView.vue')
const PipelineView = () => import('@/views/PipelineView.vue')
const DocumentsView = () => import('@/views/DocumentsView.vue')
const MessagesView = () => import('@/views/MessagesView.vue')
const NotFoundView = () => import('@/views/errors/NotFoundView.vue')
const ForbiddenView = () => import('@/views/errors/ForbiddenView.vue')
const PageExpiredView = () => import('@/views/errors/PageExpiredView.vue')
const ServerErrorView = () => import('@/views/errors/ServerErrorView.vue')
const QuickPricerMenuView = () => import('@/views/QuickPricerMenuView.vue')
const QuickPricerConventionalView = () => import('@/views/QuickPricerConventionalView.vue')
const QuickPricerNonQMView = () => import('@/views/QuickPricerNonQMView.vue')
const QuickPricerFHAView = () => import('@/views/QuickPricerFHAView.vue')
const QuickPricerVAView = () => import('@/views/QuickPricerVAView.vue')
const QuickPricerUSDAView = () => import('@/views/QuickPricerUSDAView.vue')
const ScenarioDeskView = () => import('@/views/ScenarioDeskView.vue')
const ConditionsView = () => import('@/views/ConditionsView.vue')
const LoansView = () => import('@/views/LoansView.vue')
const FundedLoansView = () => import('@/views/FundedLoansView.vue')
const LoanDetailView = () => import('@/views/LoanDetailView.vue')
const FundedLoanDetailView = () => import('@/views/FundedLoanDetailView.vue')
const BrokerPackageListView = () => import('@/views/BrokerPackageListView.vue')
const BrokerPackageView = () => import('@/views/BrokerPackageView.vue')
const ForgotPasswordView = () => import('@/views/ForgotPasswordView.vue')
const InvitationAccessView = () => import('@/views/InvitationAccessView.vue')
const LoanDetailsView = () => import('@/views/LoanDetailsView.vue')
const LoanDetailsExportView = () => import('@/views/LoanDetailsExportView.vue')
const ProfileView = () => import('@/views/profile/ProfileView.vue')
const ProfileSettingsView = () => import('@/views/profile/ProfileSettingsView.vue')
const ProfileCompanyView = () => import('@/views/profile/ProfileCompanyView.vue')
const MembersView = () => import('@/views/members/MembersView.vue')
const LoanSubmissionView = () => import('@/views/LoanSubmissionView.vue')
const ResourcesView = () => import('@/views/ResourcesView.vue')
const ExclusionaryListView = () => import('@/views/ExclusionaryListView.vue')
const AppraisalView = () => import('@/views/AppraisalView.vue')
const MortgageClauseView = () => import('@/views/MortgageClauseView.vue')
const ResourcesFormsView = () => import('@/views/ResourcesFormsView.vue')
const ResourcesProgramsView = () => import('@/views/ResourcesProgramsView.vue')
const BankStatementsSubmissionView = () => import('@/views/BankStatementsSubmissionView.vue')
const ResourcesWholesaleTurnTimesView = () => import('@/views/ResourcesWholesaleTurnTimesView.vue')
const LicensingView = () => import('@/views/LicensingView.vue')
const ResourcesUserGuidesView = () => import('@/views/ResourcesUserGuidesView.vue')
const UnionHomeMortgageLoansView = () => import('@/views/UnionHomeMortgageLoansView.vue')
const ImpersonationLoadingView = () => import('@/views/ImpersonationLoadingView.vue')
const HowToVideosView = () => import('@/views/tools/HowToVideos.vue')

const routes = [
  {
    path: '/',
    name: 'home',
    redirect: '/dashboard',
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: {
      title: 'Login - Mort-Broker',
      requiresAuth: false
    }
  },
  {
    path: '/impersonation-loading',
    name: 'impersonation-loading',
    component: ImpersonationLoadingView,
    meta: {
      title: 'Signing In - Mort-Broker',
      requiresAuth: false
    }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPasswordView,
    meta: {
      title: 'Forgot Password - Mort-Broker',
      requiresAuth: false
    }
  },
  {
    path: '/invitation/access',
    name: 'invitation-access',
    component: InvitationAccessView,
    meta: {
      title: 'Set Password - Mort-Broker',
      requiresAuth: false
    }
  },
  {
    path: '/complete-new-password',
    name: 'complete-new-password',
    component: () => import('@/views/CompleteNewPassword.vue'),
    meta: {
      title: 'Complete Password Setup - Mort-Broker'
    }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('../views/Register.vue'),
    meta: {
      title: 'Register - Mort-Broker'
    }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/quick-pricer-menu',
    name: 'quick-pricer-menu',
    component: QuickPricerMenuView,
    meta: {
      requiresAuth: true,
      title: 'Quick Pricer Menu - Mort-Broker'
    }
  },
  {
    path: '/quick-pricer/conventional',
    name: 'quick-pricer-conventional',
    component: QuickPricerConventionalView,
    meta: { 
      requiresAuth: true,
      title: 'Quick Pricer Conventional - Mort-Broker'
    }
  },
  {
    path: '/quick-pricer/nonqm',
    name: 'quick-pricer-nonqm',
    component: QuickPricerNonQMView,
    meta: { 
      requiresAuth: true,
      title: 'Quick Pricer Non-QM - Mort-Broker'
    }
  },
  {
    path: '/quick-pricer/fha',
    name: 'quick-pricer-fha',
    component: QuickPricerFHAView,
    meta: { 
      requiresAuth: true,
      title: 'Quick Pricer FHA - Mort-Broker'
    }
  },
  {
    path: '/quick-pricer/va',
    name: 'quick-pricer-va',
    component: QuickPricerVAView,
    meta: { 
      requiresAuth: true,
      title: 'Quick Pricer VA - Mort-Broker'
    }
  },
  {
    path: '/quick-pricer/usda',
    name: 'quick-pricer-usda',
    component: QuickPricerUSDAView,
    meta: { 
      requiresAuth: true,
      title: 'Quick Pricer USDA - Mort-Broker'
    }
  },
  {
    path: '/scenario-desk',
    name: 'scenario-desk',
    component: ScenarioDeskView,
    meta: { 
      requiresAuth: true,
      title: 'Scenario Desk - Mort-Broker'
    }
  },
  {
    path: '/loan-applications',
    name: 'loan-applications',
    component: () => import('@/views/LoanApplicationListView.vue'),
    meta: { 
      requiresAuth: true,
      title: 'Loan Applications - Mort-Broker'
    }
  },
  // {
  //   path: '/loan-application',
  //   name: 'loan-application',
  //   component: LoanApplicationView,
  //   meta: { requiresAuth: true },
  //   children: [
  //     {
  //       path: 'new',
  //       name: 'new-loan-application',
  //       component: () => import('@/views/NewLoanApplication.vue'),
  //       meta: { requiresAuth: true }
  //     }
  //   ]
  // },
  {
    path: '/loan-application/upload',
    name: 'upload-loan-application',
    component: LoanApplicationUploadView,
    meta: { requiresAuth: true }
  },
  {
    path: '/loan-application/create',
    name: 'create-loan-application',
    component: LoanApplicationCreateView,
    meta: { requiresAuth: true }
  },
  // {
  //   path: '/loan-application/:id',
  //   name: 'edit-loan-application',
  //   component: LoanApplicationEditView,
  //   meta: { requiresAuth: true }
  // },
  {
    path: '/loan-application/:id/view',
    name: 'view-loan-application',
    component: LoanApplicationView,
    props: { isViewMode: true },
    meta: { requiresAuth: true }
  },
  {
    path: '/loan-application/submitted/:id',
    name: 'application-submitted',
    component: () => import('@/views/ApplicationSubmittedView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/loan-application/:id/submission',
    name: 'loan-submission',
    component: LoanSubmissionView,
    meta: { 
      requiresAuth: true,
      title: 'Loan Submission - Mort-Broker'
    }
  },
  // {
  //   path: '/pipeline',
  //   name: 'pipeline',
  //   component: PipelineView,
  //   meta: { requiresAuth: true }
  // },
  {
    path: '/documents',
    name: 'documents',
    component: DocumentsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/messages',
    name: 'messages',
    component: MessagesView,
    meta: { requiresAuth: true }
  },
  {
    path: '/conditions',
    name: 'conditions',
    component: ConditionsView,
    meta: {
      title: 'Conditions - Mort-Broker',
      requiresAuth: true
    }
  },
  {
    path: '/loans',
    name: 'loans',
    component: LoansView,
    meta: {
      title: 'My Loans - Mort-Broker',
      requiresAuth: true
    }
  },
  {
    path: '/funded-loans',
    name: 'funded-loans',
    component: FundedLoansView,
    meta: {
      title: 'Funded Loans - Mort-Broker',
      requiresAuth: true
    }
  },
  {
    path: '/loans/:id',
    name: 'loan-detail',
    component: LoanDetailView,
    meta: {
      title: 'Loan Details - Mort-Broker',
      requiresAuth: true
    }
  },
  {
    path: '/funded-loans/:id',
    name: 'funded-loan-detail',
    component: FundedLoanDetailView,
    meta: {
      title: 'Funded Loan Details - Mort-Broker',
      requiresAuth: true
    }
  },
  {
    path: '/loans/:id/closing-disclosure',
    name: 'closing-disclosure',
    component: () => import('@/views/ClosingDisclosureView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Closing Disclosure Request'
    }
  },
  {
    path: '/loans/:id/request-coc',
    name: 'request-coc',
    component: () => import('@/views/request-coc/index.vue'),
    meta: {
      requiresAuth: true,
      title: 'Closing Disclosure Request'
    }
  },
  {
    path: '/loans/:id/doc-order-request',
    name: 'doc-order-request',
    component: () => import('@/views/doc-order-request/index.vue'),
    meta: {
      requiresAuth: true,
      title: 'Doc Order Request'
    }
  },
  {
    path: '/broker-packages',
    name: 'broker-packages',
    component: BrokerPackageListView,
    meta: { 
      requiresAuth: true,
      title: 'Broker Packages - Mort-Broker'
    }
  },
  {
    path: '/broker-package/:id',
    name: 'broker-package',
    component: BrokerPackageView,
    meta: { 
      requiresAuth: true,
      title: 'Broker Package - Mort-Broker'
    }
  },
  {
    path: '/loan-details/:programId/:optionRate',
    name: 'loan-details',
    component: LoanDetailsView,
    props: (route: RouteLocationNormalized) => ({
      programId: route.params.programId as string,
      optionRate: parseFloat(route.params.optionRate as string)
    }),
    meta: {
      title: 'Loan Details - Mort-Broker',
      requiresAuth: true,
      hideNav: true
    }
  },
  {
    path: '/loan-details-export/:programId/:optionRate',
    name: 'loan-details-export',
    component: LoanDetailsExportView,
    props: (route: RouteLocationNormalized) => ({
      programId: route.params.programId as string,
      optionRate: parseFloat(route.params.optionRate as string)
    }),
    meta: {
      title: 'Loan details export - Mort-Broker',
      requiresAuth: true,
      hideNav: true
    }
  },
  {
    path: '/profile',
    name: 'profile',
    component: ProfileView,
    meta: { 
      title: 'Profile - Mort-Broker',
      requiresAuth: true 
    }
  },
  {
    path: '/settings',
    name: 'settings',
    component: ProfileSettingsView,
    meta: { 
      title: 'Profile Settings - Mort-Broker',
      requiresAuth: true 
    }
  },
  {
    path: '/company',
    name: 'company',
    component: ProfileCompanyView,
    meta: { 
      title: 'Company - Mort-Broker',
      requiresAuth: true 
    }
  },
  {
    path: '/members',
    name: 'members',
    component: MembersView,
    meta: { 
      title: 'Members - Mort-Broker',
      requiresAuth: true 
    }
  },
  {
    path: '/union-home-mortgage-loans',
    name: 'union-home-mortgage-loans',
    component: UnionHomeMortgageLoansView,
    meta: {
      title: 'Union Home Mortgage Loans',
      requiresAuth: true
    }
  },
  {
    path: '/resources',
    name: 'resources',
    component: ResourcesView,
    meta: {
      title: 'Resources',
      requiresAuth: true
    }
  },
  {
    path: '/resources/exclusionary-list',
    name: 'exclusionary-list',
    component: ExclusionaryListView,
    meta: {
      title: 'Exclusionary List',
      requiresAuth: true
    }
  },
  {
    path: '/resources/appraisal',
    name: 'appraisal',
    component: AppraisalView,
    meta: {
      title: 'Approved AMCs',
      requiresAuth: true
    }
  },
  {
    path: '/resources/mortgage-clause',
    name: 'mortgage-clause',
    component: MortgageClauseView,
    meta: {
      title: 'Mortgagee Clause',
      requiresAuth: true
    }
  },
  {
    path: '/resources/forms',
    name: 'forms',
    component: ResourcesFormsView,
    meta: {
      title: 'Forms',
      requiresAuth: true
    }
  },
  {
    path: '/resources/programs',
    name: 'programs',
    component: ResourcesProgramsView,
    meta: {
      title: 'Programs',
      requiresAuth: true
    }
  },
  {
    path: '/resources/bank-statements-submission',
    name: 'bank-statements-submission',
    component: BankStatementsSubmissionView,
    meta: {
      title: 'Bank Statements Submission',
      requiresAuth: true
    }
  },
  {
    path: '/resources/wholesale-turntimes',
    name: 'wholesale-turntimes',
    component: ResourcesWholesaleTurnTimesView,
    meta: {
      title: 'Wholesale Turn Times',
      requiresAuth: true
    }
  },
  {
    path: '/resources/licensing',
    name: 'licensing',
    component: LicensingView,
    meta: {
      title: 'Licensing',
      requiresAuth: true
    }
  },
  {
    path: '/resources/guide',
    name: 'resources-guide',
    component: ResourcesUserGuidesView,
    meta: {
      title: 'Guide',
      requiresAuth: true
    }
  },
  {
    path: '/tools/how-to-videos',
    name: 'how-to-videos',
    component: HowToVideosView,
    meta: {
      title: 'How-to Videos',
      requiresAuth: true
    }
  },
  {
    path: '/403',
    name: 'forbidden',
    component: ForbiddenView,
    meta: { requiresErrorTrigger: true },
  },
  {
    path: '/419',
    name: 'page-expired',
    component: PageExpiredView,
    meta: { requiresErrorTrigger: true },
  },
  {
    path: '/500',
    name: 'server-error',
    component: ServerErrorView,
    meta: { requiresErrorTrigger: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore()
  const isAuthRoute = to.matched.some(record => record.meta.requiresAuth)
  const isLoginPage = to.path === '/login'
  const isErrorPage = to.matched.some(record => record.meta.requiresErrorTrigger)

  // Wait for auth initialization if not initialized
  if (!auth.isInitialized) {
    try {
      await auth.initialize()
    } catch (error) {
      console.error('Auth initialization failed in router guard:', error)
    }
  }

  // Prevent direct access to error pages
  if (isErrorPage && from.name === undefined) {
    // If there's a previous page in history, go back
    if (window.history.length > 1) {
      window.history.back()
    } else {
      // If no history, fallback to dashboard
      next({ path: '/dashboard' })
    }
    return
  }

  // Handle authenticated routes
  if (isAuthRoute) {
    if (!auth.isAuthenticated) {
      // Store the intended destination
      const redirect = to.fullPath === '/' ? undefined : to.fullPath
      next({ path: '/login', query: redirect ? { redirect } : undefined })
    } else {
      next()
    }
  }
  // Handle login page
  else if (isLoginPage && auth.isAuthenticated) {
    next({ path: '/dashboard' })
  }
  // Allow all other routes
  else {
    next()
  }
})

export default router 